import type { PoiCategoryOption } from '@/features/poi/poi-basic-info-form'
import type { PoiFormValues } from '@/features/poi/poi-form-model/types'
import type { ITenantDestination } from '@/shared/api/types/tenant-destination-types'
import type { IPOI, IPoiCategory, PoiType } from '@/shared/api/types/poi-types'
import { createEmptyPoiTranslation } from '@/features/poi/poi-form-model/translations'
import { POI_TYPES } from '@/shared/constants/poi-type'
import { mergeTranslationsForLanguages } from '@/shared/utils/language-utils'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

/**
 * Form values of an existing POI.
 *
 * VI: POI chỉ có localizedData cho ngôn ngữ đã dịch; gộp với danh sách ngôn ngữ của tenant để ngôn ngữ thêm sau vẫn có tab trống để nhập.
 * EN: A POI only carries localizedData for languages already translated; merge with the tenant's list so languages added later still get an empty tab.
 */
export function derivePoiFormValues(poi: IPOI, supportedLanguages: string[]): PoiFormValues {
  return {
    basicInfo: {
      category: poi.categoryData?.id ?? poi.category,
      type: poi.type,
      popupAd: poi.popupAd,
      range: poi.range,
    },
    location: {
      latitude: poi.latitude,
      longitude: poi.longitude,
      range: poi.range,
      address: poi.address ?? '',
    },
    translations: mergeTranslationsForLanguages(
      supportedLanguages,
      poi.localizedData,
      createEmptyPoiTranslation,
    ),
    images: poi.banner.map((url) => ({ url, previewUrl: url, status: 'success' as const })),
    tenantDestinationId: poi.tenantDestinationId ?? '',
  }
}

/**
 * Identifies "this POI payload + these tenant languages". The edit form re-hydrates only when the signature
 * changes, so a React Query refetch with new data refreshes the form but an identical render does not wipe edits.
 *
 * VI: supportedLanguages nằm trong signature để khi ngôn ngữ tenant về sau dữ liệu POI, form hydrate lại và bổ sung tab còn thiếu. | EN: supportedLanguages is part of the signature so the form re-hydrates once tenant languages arrive after the POI payload.
 */
export function buildPoiHydrationSignature(
  poi: IPOI,
  formLanguageCode: string,
  supportedLanguages: string[],
): string {
  const apiSnapshot = JSON.stringify({
    localizedData: poi.localizedData,
    category: poi.categoryData?.id ?? poi.category,
    type: poi.type,
    popupAd: poi.popupAd,
    range: poi.range,
    latitude: poi.latitude,
    longitude: poi.longitude,
    address: poi.address ?? '',
    banner: poi.banner,
    tenantDestinationId: poi.tenantDestinationId ?? '',
  })
  return `edit:${poi.id}:${formLanguageCode || 'default'}:${supportedLanguages.join(',')}:${apiSnapshot}`
}

/** Centre of a destination, or `null` when it has no valid coordinates (the map then keeps its own default). */
export function resolveDestinationCenter(
  destination: ITenantDestination | undefined,
): { latitude: number; longitude: number } | null {
  const latitude = destination?.centerLat
  const longitude = destination?.centerLng
  if (typeof latitude !== 'number' || !Number.isFinite(latitude)) {
    return null
  }
  if (typeof longitude !== 'number' || !Number.isFinite(longitude)) {
    return null
  }
  return { latitude, longitude }
}

/** Category dropdown options, deduplicated by id and labelled in the form language (then the tenant default). */
export function buildCategoryOptions(
  categories: IPoiCategory[],
  preferredLanguages: string[],
): PoiCategoryOption[] {
  const options = new Map<string, PoiCategoryOption>()
  for (const category of categories) {
    const value = category.id.trim()
    if (!value || options.has(value)) {
      continue
    }
    const label = resolvePoiCategoryName(category, preferredLanguages)
    options.set(value, {
      value,
      label: label || value,
      ...(category.icon ? { icon: category.icon } : {}),
    })
  }
  return Array.from(options.values())
}

/**
 * VI: Chuẩn hóa danh sách type về MAJOR/MINOR, bỏ mọi giá trị legacy từ API; luôn giữ type đang chọn để Select không mất giá trị; MAJOR đứng trước.
 * EN: Normalizes type options to MAJOR/MINOR only, ignoring legacy API values; always keeps the current type so the Select never loses its value; MAJOR first.
 */
export function buildTypeOptions(apiTypes: string[], currentType: PoiType | ''): PoiType[] {
  const options = new Set<PoiType>()
  for (const item of apiTypes) {
    const value = item.trim().toLowerCase()
    if (value === POI_TYPES.MAJOR || value === POI_TYPES.MINOR) {
      options.add(value)
    }
  }
  if (currentType) {
    options.add(currentType)
  }
  if (options.size < 1) {
    return [POI_TYPES.MAJOR, POI_TYPES.MINOR]
  }
  return Array.from(options).sort((a, b) =>
    a === POI_TYPES.MAJOR ? -1 : b === POI_TYPES.MAJOR ? 1 : 0,
  )
}
